/* 
  
  MF Simulator: Homepage
  
*/

import React, { useLayoutEffect, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { trackPageView, trackClick } from '../utils/analytics';

const Main = (props) => {

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  // Set the page title
  const page_title = props.config['app-title'];
  document.title = page_title;

  // Detect the language before render
  // useLayoutEffect will block render until complete so be careful
  useLayoutEffect(() => {
    // Set the language
    i18n.changeLanguage(props.lan);
  }, [i18n, props.lan]);

  // Set the flag when the welcome page is visited
  useEffect(() => {
    // Use sessionStorage for session-based tracking
    sessionStorage.setItem('hasVisitedWelcome', 'true');
    
    // Track page view
    trackPageView(props.hreflang);
  }, [props.hreflang]);

  const handleButtonClick = () => {
    // Track click event
    trackClick({
      componentName: 'main hero',
      type: 'button_click',
      linkText: 'See the difference',
      linkUrl: `${window.location.origin}/${props.hreflang}/simulator`,
      outbound: false
    });
    
    // Navigate to the simulator page
    navigate(`/${props.hreflang}/simulator`);
  };

  return(
    <div className={`d-flex flex-column align-items-center bgblue position-relative text-white min-vh-100 lang-${props.lan}`}>
      
      {/* Desktop layout container */}
      <div className="container desktop-layout flex-grow-1 d-none d-lg-flex">
        <div className="row align-items-center w-100">
          {/* Left column with packshot on desktop */}
          <div className="col-lg-5 desktop-packshot-col d-flex align-items-center justify-content-center">
            <img src={props.config?.images?.[props.hreflang]?.packshot || "/img/packshot_en_us.png"} alt="Acuvue" className="packshot-img" />
          </div>
          
          {/* Right column with content on desktop */}
          <div className="col-lg-7 desktop-content-col d-flex flex-column">
            <div className="mb-auto"></div> {/* Spacer to push content down slightly */}
            <h1 id="home-h1" className="text-center text-lg-start mb-3"><Trans i18nKey="main.h1" components={{ sup: <sup /> }} /> </h1>
            <h2 id="home-h2" className="text-center text-lg-start mb-4"><Trans i18nKey="main.h2" components={{ sup: <sup /> }} /></h2>
            <div className="d-flex justify-content-center justify-content-lg-start mb-5">
              <button className="btn acu-btn fw-bold" onClick={handleButtonClick}>
                <Trans i18nKey="main.button" /> <FontAwesomeIcon icon={['fas', 'arrow-right']} />
              </button>
            </div>
            <div className="mb-auto"></div> {/* Spacer to push content up slightly */}
          </div>
        </div>
      </div>
      
      {/* Mobile layout - only visible on mobile */}
      <div className="d-lg-none flex-grow-0 pt-4 container">
        <h1 id="home-h1" className="text-center mb-3"><Trans i18nKey="main.h1" components={{ sup: <sup /> }} /> </h1>
        <h2 id="home-h2" className="text-center mb-3"><Trans i18nKey="main.h2" components={{ sup: <sup /> }} /></h2>

        {/* Row that's side-by-side only in landscape mode on mobile */}
        <div className="row align-items-center mb-3 gx-0">
          {/* Takes full width by default, 8 columns in landscape orientation */}
          <div className="col-12 col-landscape-8 order-2 order-landscape-1 mb-3 mb-landscape-0">
            <p id="home-orientation" className="text-center">
              <Trans i18nKey="main.orientation" />
            </p>
          </div>
          
          {/* Takes full width by default, 4 columns in landscape orientation */}
          <div id="start-button" className="col-12 col-landscape-4 d-flex justify-content-center justify-content-landscape-end order-1 order-landscape-2 mb-3 mb-landscape-0">
            <button className="btn acu-btn fw-bold" onClick={handleButtonClick}>
              <Trans i18nKey="main.button" /> <FontAwesomeIcon icon={['fas', 'arrow-right']} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile packshot - only visible on mobile */}
      <div className="packshot-container d-flex d-lg-none align-items-center py-3">
        <img src={props.config?.images?.[props.hreflang]?.packshot || "/img/packshot.png"} alt="Acuvue" className="packshot-img" />
      </div>
      
      <div className="footer-content w-100 pb-4">
        {/* Desktop footer - in one line */}
        <div className="d-none d-lg-flex justify-content-center align-items-center footer-row">
          <p className="home-disclaimer mb-0"><Trans i18nKey="main.disclaimer1" /></p>
          <p className="home-disclaimer mb-0"><Trans i18nKey="main.disclaimer2" /></p>
          <p id="home-mlr" className="mb-0"><Trans i18nKey="mlr.rev_num" /></p>
        </div>
        
        {/* Mobile footer - stacked */}
        <div className="d-lg-none">
          <p className="home-disclaimer text-center"><Trans i18nKey="main.disclaimer1" /></p>
          <p className="text-center home-disclaimer"><Trans i18nKey="main.disclaimer2" /></p>
          <p id="home-mlr" className="text-center mb-0"><Trans i18nKey="mlr.rev_num" /></p>
        </div>
      </div>
    </div>
  )

}

export default Main;